import React, { useState } from 'react';
import { useLinkedIn } from '../../context/LinkedInContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import CreateOpportunityModal from './CreateOpportunityModal';
import { createLinkedInOpportunity, hasOpenLinkedInOpportunity } from '../../utils/linkedinToPipeline';

const ProspectInbox = () => {
  const { prospects, updateProspectStatus } = useLinkedIn();
  const linkedinContext = useLinkedIn();
  const {
    companies,
    contacts,
    deals,
    addCompany,
    addContact,
    addDeal,
    pipelineStages
  } = useCRM();
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [editingProspect, setEditingProspect] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [draggedProspectId, setDraggedProspectId] = useState(null);

  const lanes = ['Ny', 'Klar for kontakt', 'Kontaktet', 'Dialog', 'Møte booket'];
  const displayLanes = [...lanes, 'Avvist'];

  const normalizeStatus = status => {
    const normalizedStatus = String(status || '').trim().toLowerCase();

    const statusMap = {
      ny: 'Ny',
      klar: 'Klar for kontakt',
      'klar for kontakt': 'Klar for kontakt',
      kontaktet: 'Kontaktet',
      dialog: 'Dialog',
      'møte booket': 'Møte booket',
      'mote booket': 'Møte booket',
      avvist: 'Avvist'
    };

    return statusMap[normalizedStatus] || 'Ny';
  };

  const getProspectsByStatus = (status) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return prospects.filter((prospect) => {
      const prospectStatus = normalizeStatus(prospect.status);
      const matchesStatus = prospectStatus === status;
      const matchesSearch = !normalizedSearch || [
        prospect.full_name,
        prospect.company_name,
        prospect.title
      ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));

      return matchesStatus && matchesSearch &&
        (statusFilter === 'Alle' || prospectStatus === normalizeStatus(statusFilter));
    });
  };

  const stageId = pipelineStages?.[0]?.stage_id || pipelineStages?.[0]?.id || 'stage-1';

  const isResearchProspect = prospect =>
    prospect.title === 'Research' || prospect.full_name === 'Finn kontaktperson';

  const isSearchUrl = url => String(url || '').includes('/search/results/people');

  const hasOpenOpportunity = prospect => {
    if (!prospect) return false;

    const normalizedCompanyName = String(prospect.company_name || '').trim().toLowerCase();
    const company = companies.find(item =>
      String(item.name || '').trim().toLowerCase() === normalizedCompanyName
    );
    const companyId = company?.company_id || company?.id;
    const normalizedLinkedInUrl = String(prospect.linkedin_url || '').trim().replace(/\/+$/, '').toLowerCase();
    const normalizedName = String(prospect.full_name || '').trim().toLowerCase();

    const contact = contacts.find(item =>
      normalizedLinkedInUrl &&
      String(item.linkedin_url || '').trim().replace(/\/+$/, '').toLowerCase() === normalizedLinkedInUrl
    ) || contacts.find(item =>
      String(item.name || '').trim().toLowerCase() === normalizedName &&
      (item.company_id || item.companyId) === companyId
    );

    const contactId = contact?.contact_id || contact?.id;
    const title = `LinkedIn-mulighet: ${prospect.full_name}`;
    const closedStatuses = ['lukket', 'tapt', 'lost', 'closed', 'closed_lost', 'closed_won', 'vunnet', 'won'];

    const legacyMatch = deals.some(deal =>
      deal.company_id === companyId &&
      deal.contact_id === contactId &&
      deal.title === title &&
      !closedStatuses.includes(String(deal.status || '').trim().toLowerCase())
    );

    return legacyMatch || hasOpenLinkedInOpportunity({
      prospect,
      deals,
      companies,
      contacts
    });
  };

  const openLinkedIn = prospect => {
    if (!prospect.linkedin_url) return;
    window.open(prospect.linkedin_url, '_blank', 'noopener,noreferrer');
  };

  const copyLinkedIn = async prospect => {
    if (!prospect.linkedin_url) return;

    try {
      await navigator.clipboard.writeText(prospect.linkedin_url);
      setFeedback(isSearchUrl(prospect.linkedin_url) ? 'LinkedIn-søk kopiert' : 'LinkedIn-lenke kopiert');
    } catch {
      setFeedback('Kunne ikke kopiere LinkedIn-lenken.');
    }
  };

  const openOpportunityModal = prospect => {
    setFeedback('');
    setSelectedProspect(prospect);
    setIsOpportunityModalOpen(true);
  };

  const handleCreateOpportunity = async dealInput => {
    if (!selectedProspect) return;

    setIsSubmitting(true);
    setFeedback('');

    const result = await createLinkedInOpportunity({
      prospect: selectedProspect,
      dealInput,
      companies,
      contacts,
      deals,
      addCompany,
      addContact,
      addDeal
    });

    if (result.duplicate) {
      const statusResult = await updateProspectStatus(selectedProspect.id, 'Dialog');
      setIsSubmitting(false);
      setIsOpportunityModalOpen(false);
      setSelectedProspect(null);
      setFeedback(statusResult?.success
        ? 'Salgsmulighet finnes allerede i Pipeline'
        : 'Salgsmulighet finnes allerede i Pipeline. Prospektstatus kunne ikke oppdateres.');
      return;
    }

    if (!result.success) {
      setIsSubmitting(false);
      setFeedback(result.error || 'Salgsmuligheten kunne ikke opprettes.');
      return;
    }

    const statusResult = await updateProspectStatus(selectedProspect.id, 'Dialog');
    setIsSubmitting(false);

    setIsOpportunityModalOpen(false);
    setSelectedProspect(null);
    setFeedback(statusResult?.success
      ? 'Salgsmulighet opprettet og prospektet flyttet til Dialog.'
      : 'Salgsmulighet opprettet, men prospektstatus kunne ikke oppdateres.');
  };

  const handleDragStart = (event, prospect) => {
    setDraggedProspectId(prospect.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(prospect.id));
  };

  const handleDragEnd = () => {
    setDraggedProspectId(null);
  };

  const handleDrop = async (event, targetLane) => {
    event.preventDefault();

    const draggedId = event.dataTransfer.getData('text/plain') || draggedProspectId;
    const prospect = prospects.find(item => String(item.id) === String(draggedId));

    setDraggedProspectId(null);

    if (!prospect) {
      setFeedback('Prospektet kunne ikke flyttes.');
      return;
    }

    const currentStatus = normalizeStatus(prospect.status);
    if (currentStatus === targetLane) return;

    const result = await updateProspectStatus(prospect.id, targetLane);
    if (!result?.success) {
      setFeedback(result?.error || 'Prospektstatus kunne ikke oppdateres.');
    }
  };

  const openEditor = prospect => {
    setEditingProspect({ ...prospect });
  };

  const saveProspect = async event => {
    event.preventDefault();

    if (!linkedinContext.updateProspect) {
      setFeedback('Redigering støttes ikke av LinkedIn-contexten ennå.');
      return;
    }

    setIsSubmitting(true);
    const result = await linkedinContext.updateProspect(editingProspect.id, editingProspect);
    setIsSubmitting(false);

    if (!result?.success) {
      setFeedback(result?.error || 'Prospektet kunne ikke oppdateres.');
      return;
    }

    setEditingProspect(null);
    setFeedback('Prospekt oppdatert.');
  };

  const sendToOutreach = async prospect => {
    if (!linkedinContext.ensureOutreachTask) {
      setFeedback('Outreach-task støttes ikke av LinkedIn-contexten ennå.');
      return;
    }

    const result = await linkedinContext.ensureOutreachTask(prospect);
    setFeedback(result?.success
      ? 'Oppgave lagt til i Dagens Outreach.'
      : result?.error || 'Oppgaven kunne ikke opprettes.');
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      {feedback && (
        <div className="fixed right-6 top-6 z-[10000] rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-700 shadow-lg">
          {feedback}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          placeholder="Søk på navn, selskap eller tittel"
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
        />
        <select
          value={statusFilter}
          onChange={event => setStatusFilter(event.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 outline-none focus:border-blue-500"
        >
          <option value="Alle">Alle statuser</option>
          {displayLanes.map(status => <option key={status} value={status}>{status}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs font-medium text-blue-700">
        LinkedIn Outreach er manuell. Systemet hjelper med søk, tekst og oppfølging, men scraper ikke LinkedIn og sender ikke meldinger automatisk.
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4 h-full min-h-[500px]">
        {displayLanes.map((lane) => (
          <div key={lane} className="min-w-[280px] w-[280px] flex flex-col bg-slate-100/50 rounded-2xl p-3 border border-slate-200/50">
            <div className="flex justify-between items-center mb-3 px-2">
              <h3 className="font-bold text-slate-700 text-sm uppercase tracking-wide">{lane}</h3>
              <span className="bg-white px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-500 shadow-sm">
                {getProspectsByStatus(lane).length}
              </span>
            </div>
          
            <div
              className="flex-1 space-y-3 overflow-y-auto custom-scrollbar pr-1"
              onDragOver={event => event.preventDefault()}
              onDrop={event => handleDrop(event, lane)}
            >
              {getProspectsByStatus(lane).map((prospect) => {
                const research = isResearchProspect(prospect);
                const prospectStatus = normalizeStatus(prospect.status);
                const opportunityExists = hasOpenOpportunity(prospect);

                return (
                  <div
                    key={prospect.id}
                    draggable
                    onDragStart={event => handleDragStart(event, prospect)}
                    onDragEnd={handleDragEnd}
                    className={`bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group cursor-grab active:cursor-grabbing relative overflow-hidden ${
                      String(draggedProspectId) === String(prospect.id) ? 'opacity-50' : ''
                    }`}
                  >
                    <div className={`absolute top-0 left-0 w-1 h-full ${prospect.priority === 'Høy' ? 'bg-rose-500' : 'bg-blue-500'}`} />
                
                    <div className="flex justify-between items-start mb-2 pl-2">
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">
                          {research ? prospect.company_name : prospect.full_name}
                        </h4>
                        <p className="text-xs text-slate-500">
                          {research ? 'Finn kontaktperson · Research' : prospect.title}
                        </p>
                      </div>
                      {prospect.linkedin_url && (
                        <a 
                          href={prospect.linkedin_url} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SafeIcon icon="👔" className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                
                    <div className="pl-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                        <SafeIcon icon="🏢" className="w-3 h-3" /> {prospect.company_name}
                      </p>

                      {research && (
                        <p className="mt-2 text-xs text-slate-600">{prospect.notes}</p>
                      )}

                      {prospect.linkedin_url && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => openLinkedIn(prospect)}
                            className="rounded-lg bg-blue-50 px-2 py-1.5 text-[11px] font-bold text-blue-700 hover:bg-blue-100"
                          >
                            {research ? 'Åpne LinkedIn-søk' : 'Åpne LinkedIn'}
                          </button>
                          <button
                            type="button"
                            onClick={() => copyLinkedIn(prospect)}
                            className="rounded-lg bg-slate-100 px-2 py-1.5 text-[11px] font-bold text-slate-700 hover:bg-slate-200"
                          >
                            {research ? 'Kopier søk' : 'Kopier LinkedIn-lenke'}
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => openEditor(prospect)}
                        className="mt-3 w-full rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 hover:bg-amber-100"
                      >
                        Rediger
                      </button>

                      <select
                        aria-label={`Status for ${prospect.full_name}`}
                        value={prospectStatus}
                        onChange={(event) => updateProspectStatus(prospect.id, event.target.value)}
                        onClick={(event) => event.stopPropagation()}
                        className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 outline-none focus:border-blue-500"
                      >
                        {lanes.map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                        <option value="Avvist">Avvist</option>
                      </select>

                      <div className="mt-2 flex flex-wrap gap-1">
                        <button type="button" onClick={() => updateProspectStatus(prospect.id, 'Klar for kontakt')} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          Godkjenn
                        </button>
                        <button type="button" onClick={() => updateProspectStatus(prospect.id, 'Kontaktet')} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          Marker kontaktet
                        </button>
                        <button type="button" onClick={() => updateProspectStatus(prospect.id, 'Dialog')} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          Sett i dialog
                        </button>
                        <button type="button" onClick={() => updateProspectStatus(prospect.id, 'Møte booket')} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          Møte booket
                        </button>
                        <button type="button" onClick={() => updateProspectStatus(prospect.id, 'Avvist')} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                          Avvis
                        </button>
                      </div>

                      {(prospectStatus === 'Klar for kontakt' || (research && prospectStatus === 'Ny')) && (
                        <button
                          type="button"
                          onClick={() => sendToOutreach(prospect)}
                          className="mt-3 w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-bold text-white hover:bg-indigo-700"
                        >
                          Send til dagens outreach
                        </button>
                      )}

                      {!research && (
                        <button
                          type="button"
                          onClick={event => {
                            event.stopPropagation();
                            openOpportunityModal(prospect);
                          }}
                          className="mt-3 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800 transition-colors"
                        >
                          {opportunityExists ? 'Allerede i Pipeline' : 'Konverter til pipeline'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {getProspectsByStatus(lane).length === 0 && (
                <div className="text-center py-8 text-slate-300 border-2 border-dashed border-slate-200 rounded-xl">
                  <p className="text-xs font-medium">Tomt</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editingProspect && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/40 p-4">
          <form onSubmit={saveProspect} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Rediger LinkedIn-prospekt</h2>
              <button type="button" onClick={() => setEditingProspect(null)} className="text-sm font-semibold text-slate-500">Lukk</button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ['full_name', 'Navn'],
                ['title', 'Tittel'],
                ['company_name', 'Selskap'],
                ['linkedin_url', 'LinkedIn URL']
              ].map(([field, label]) => (
                <label key={field} className="text-xs font-semibold text-slate-600">
                  {label}
                  <input
                    value={editingProspect[field] || ''}
                    onChange={event => setEditingProspect(prev => ({ ...prev, [field]: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-500"
                  />
                </label>
              ))}
            </div>

            <label className="mt-3 block text-xs font-semibold text-slate-600">
              Notater
              <textarea
                value={editingProspect.notes || ''}
                onChange={event => setEditingProspect(prev => ({ ...prev, notes: event.target.value }))}
                rows="4"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-blue-500"
              />
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditingProspect(null)} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
                Avbryt
              </button>
              <button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                {isSubmitting ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </form>
        </div>
      )}

      <CreateOpportunityModal
        isOpen={isOpportunityModalOpen}
        prospect={selectedProspect}
        stageId={stageId}
        onClose={() => {
          if (!isSubmitting) {
            setIsOpportunityModalOpen(false);
            setSelectedProspect(null);
          }
        }}
        onSubmit={handleCreateOpportunity}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ProspectInbox;